import {
  adminCloseListing,
  adminCreateAvailabilityException,
  adminDeleteAvailabilityException,
  adminOpenListing,
  adminQueryAvailabilityExceptions,
  adminShowListing,
  adminUpdateListing,
  adminUploadImage,
} from '../../util/api';

// Which backend the listing editor talks to.
//
// The editor's data layer (EditListingPage.duck.js and the profile photo part of
// ProfileSettingsPage.duck.js) is written against the small API surface below, so the
// same thunks, reducers and wizard panels serve both an expert editing their own listing
// and an admin editing any listing (AdminEditListingPage):
//
//   OWN_LISTING_API   – the Marketplace API through the SDK (own listings, current user)
//   ADMIN_LISTING_API – admin-only server endpoints backed by the Integration API
//                       (server/api/admin/listing-*.js, image-upload.js)
//
// Both return SDK-shaped (Transit) responses, so callers never need to know which one
// they are talking to.
export const OWN_LISTING_API = 'own';
export const ADMIN_LISTING_API = 'admin';

const uuidString = id => (id && id.uuid ? id.uuid : id);

const ownListingApi = sdk => ({
  show: (params, queryParams) => sdk.ownListings.show({ ...params, ...queryParams }),
  update: (params, queryParams) => sdk.ownListings.update(params, queryParams),
  close: (params, queryParams) => sdk.ownListings.close(params, queryParams),
  open: (params, queryParams) => sdk.ownListings.open(params, queryParams),
  uploadImage: (params, queryParams) => sdk.images.upload(params, queryParams),
  queryAvailabilityExceptions: params => sdk.availabilityExceptions.query(params),
  createAvailabilityException: (params, queryParams) =>
    sdk.availabilityExceptions.create(params, queryParams),
  deleteAvailabilityException: (params, queryParams) =>
    sdk.availabilityExceptions.delete(params, queryParams),
});

// The admin editor shows the listing author as the "current user" of the details panel
// (name, avatar, privacy settings), so listing responses must carry the author's avatar.
const AUTHOR_AVATAR_FIELDS = ['variants.square-small', 'variants.square-small2x'];
const withAuthorAvatar = (queryParams = {}) => ({
  ...queryParams,
  include: [...new Set([...(queryParams.include || []), 'author', 'author.profileImage'])],
  'fields.image': [...new Set([...(queryParams['fields.image'] || []), ...AUTHOR_AVATAR_FIELDS])],
});

// The listing id travels in the URL of the admin endpoints, hence the destructuring.
const adminListingApi = {
  show: ({ id }, queryParams) => adminShowListing(uuidString(id), withAuthorAvatar(queryParams)),
  update: ({ id, ...listing }, queryParams) =>
    adminUpdateListing(uuidString(id), listing, withAuthorAvatar(queryParams)),
  close: ({ id }, queryParams) => adminCloseListing(uuidString(id), withAuthorAvatar(queryParams)),
  open: ({ id }, queryParams) => adminOpenListing(uuidString(id), withAuthorAvatar(queryParams)),
  uploadImage: ({ image }, queryParams) => adminUploadImage(image, queryParams),
  queryAvailabilityExceptions: ({ listingId, ...params }) =>
    adminQueryAvailabilityExceptions(uuidString(listingId), params),
  createAvailabilityException: ({ listingId, ...params }) =>
    adminCreateAvailabilityException(uuidString(listingId), params),
  deleteAvailabilityException: ({ id }) => adminDeleteAvailabilityException(uuidString(id)),
};

/**
 * Resolve the listing API for a thunk: `listingApi` is one of the constants above
 * (undefined means the expert's own listings) and `sdk` the thunk's extra argument.
 */
export const getListingApi = (listingApi, sdk) =>
  listingApi === ADMIN_LISTING_API ? adminListingApi : ownListingApi(sdk);
